(() => {
  'use strict';

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

  const emit = (name, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'bamptons_interaction', interaction: name, ...detail });
    window.dispatchEvent(new CustomEvent('bamptons:interaction', { detail: { name, ...detail } }));
  };

  const bindMenu = () => {
    const toggle = qs('[data-menu-toggle]');
    const menu = qs('[data-mobile-nav]');
    if (!toggle || !menu || menu.dataset.bound === 'true') return;
    menu.dataset.bound = 'true';
    const closeButtons = qsa('[data-menu-close]', menu);
    let lastFocus = null;

    const open = () => {
      lastFocus = document.activeElement;
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('menu-open');
      setTimeout(() => qs('a, button, summary', menu)?.focus(), 30);
    };
    const close = () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
      lastFocus?.focus?.();
    };

    toggle.addEventListener('click', open);
    closeButtons.forEach((button) => button.addEventListener('click', close));
    menu.addEventListener('click', (event) => {
      if (event.target === menu || event.target.closest('a')) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) close();
    });
  };

  const bindDesktopDropdown = () => {
    qsa('.nav-dropdown').forEach((details) => {
      if (details.dataset.bound === 'true') return;
      details.dataset.bound = 'true';
      document.addEventListener('click', (event) => {
        if (details.open && !details.contains(event.target)) details.removeAttribute('open');
      });
      details.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          details.removeAttribute('open');
          qs('summary', details)?.focus();
        }
      });
    });
  };

  const bindQuoteDrawer = () => {
    const drawer = qs('[data-quote-drawer]');
    if (!drawer || drawer.dataset.bound === 'true') return;
    drawer.dataset.bound = 'true';
    const dialog = qs('[role="dialog"]', drawer);
    const form = qs('#BamptonsQuoteDrawerForm', drawer);
    const steps = qsa('[data-quote-step]', drawer);
    const progress = qsa('[data-progress-segment]', drawer);
    const serviceInput = qs('[data-service-input]', drawer);
    const serviceButtons = qsa('[data-service-option]', drawer);
    let currentStep = 1;
    let lastFocus = null;

    const render = () => {
      steps.forEach((step) => step.classList.toggle('is-active', Number(step.dataset.quoteStep) === currentStep));
      progress.forEach((segment, index) => segment.classList.toggle('is-active', index < currentStep));
      qs('[data-quote-step-label]', drawer)?.replaceChildren(document.createTextNode(`Step ${currentStep} of ${steps.length}`));
      dialog?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const selectService = (value) => {
      if (!value) return;
      if (serviceInput) serviceInput.value = value;
      serviceButtons.forEach((button) => {
        const selected = button.dataset.serviceOption === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    };

    const open = (service = '') => {
      lastFocus = document.activeElement;
      if (service) selectService(service);
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('quote-open');
      currentStep = service ? 2 : 1;
      render();
      setTimeout(() => qs('button, input, select, textarea', qs('.quote-step.is-active', drawer))?.focus(), 40);
      emit('quote_open', { service: service || serviceInput?.value || '' });
    };

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('quote-open');
      lastFocus?.focus?.();
    };

    const validateCurrentStep = () => {
      const active = qs('.quote-step.is-active', drawer);
      if (!active) return true;
      const fields = qsa('input[required], select[required], textarea[required]', active);
      for (const field of fields) {
        if (!field.checkValidity()) {
          field.reportValidity();
          field.focus();
          return false;
        }
      }
      if (currentStep === 1 && serviceInput && !serviceInput.value) {
        serviceButtons[0]?.focus();
        qs('[data-service-error]', drawer)?.removeAttribute('hidden');
        return false;
      }
      qs('[data-service-error]', drawer)?.setAttribute('hidden', '');
      return true;
    };

    qsa('[data-quote-open]').forEach((trigger) => {
      if (trigger.dataset.quoteBound === 'true') return;
      trigger.dataset.quoteBound = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        open(trigger.dataset.service || '');
      });
    });
    qsa('[data-quote-close]', drawer).forEach((button) => button.addEventListener('click', close));
    drawer.addEventListener('click', (event) => {
      if (event.target.matches('[data-quote-backdrop]')) close();
    });
    serviceButtons.forEach((button) => button.addEventListener('click', () => {
      selectService(button.dataset.serviceOption || '');
      qs('[data-service-error]', drawer)?.setAttribute('hidden', '');
    }));
    qsa('[data-quote-next]', drawer).forEach((button) => button.addEventListener('click', () => {
      if (!validateCurrentStep()) return;
      currentStep = Math.min(steps.length, currentStep + 1);
      render();
    }));
    qsa('[data-quote-back]', drawer).forEach((button) => button.addEventListener('click', () => {
      currentStep = Math.max(1, currentStep - 1);
      render();
    }));
    form?.addEventListener('submit', () => emit('quote_submit', { service: serviceInput?.value || '' }));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    const queryService = new URLSearchParams(window.location.search).get('service');
    if (queryService) selectService(queryService);
    if (qs('.form-success', drawer) || drawer.dataset.quoteSuccess === 'true') open(serviceInput?.value || '');
  };

  const bindBeforeAfter = () => {
    qsa('[data-before-after]').forEach((comparison) => {
      if (comparison.dataset.bound === 'true') return;
      comparison.dataset.bound = 'true';
      const range = qs('[data-before-after-range]', comparison);
      if (!range) return;
      const update = () => {
        const value = Math.max(0, Math.min(100, Number(range.value) || 50));
        comparison.style.setProperty('--before-position', `${value}%`);
        range.setAttribute('aria-valuetext', `${value}% of the before image visible`);
      };
      range.addEventListener('input', update, { passive: true });
      range.addEventListener('change', update, { passive: true });
      update();
    });
  };

  const bindProjectFilters = () => {
    qsa('[data-project-filters]').forEach((group) => {
      if (group.dataset.bound === 'true') return;
      group.dataset.bound = 'true';
      const cards = qsa('[data-project-category]');
      qsa('[data-filter]', group).forEach((button) => {
        button.addEventListener('click', () => {
          const filter = button.dataset.filter || 'all';
          qsa('[data-filter]', group).forEach((item) => item.classList.toggle('is-active', item === button));
          cards.forEach((card) => {
            const categories = (card.dataset.projectCategory || '').split(' ');
            card.hidden = filter !== 'all' && !categories.includes(filter);
          });
          emit('project_filter', { filter });
        });
      });
    });
  };

  const bindTracking = () => {
    qsa('[data-track]').forEach((item) => {
      if (item.dataset.trackBound === 'true') return;
      item.dataset.trackBound = 'true';
      item.addEventListener('click', () => emit(item.dataset.track || 'click', {
        label: item.textContent.trim().slice(0, 80),
        page: window.location.pathname
      }));
    });
  };

  const bindReveal = () => {
    const items = qsa('[data-reveal]:not([data-reveal-bound])');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    items.forEach((item) => {
      item.dataset.revealBound = 'true';
      observer.observe(item);
    });
  };

  const init = () => {
    bindMenu();
    bindDesktopDropdown();
    bindQuoteDrawer();
    bindBeforeAfter();
    bindProjectFilters();
    bindTracking();
    bindReveal();
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
